import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../../services/supabaseClient.js';
import { CertificatesService } from '../../services/certificatesService.js';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Alert, AlertDescription } from '../../components/ui/alert';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Input } from '../../components/ui/input';
import {
  FileText, Save, Download, Eye, Code2, Search,
  RefreshCw, Copy, CheckCheck, Mail, AlertCircle, Shuffle, Upload, Plus, X, Pencil,
} from 'lucide-react';

// ─── Constants ────────────────────────────────────────────────────────────────

const TEMPLATE_TYPES = [
  { value: 'certificado',  label: 'Certificado de Seguro',  templateName: 'certificado_pdf'      },
  { value: 'cotizacion',   label: 'Cotización de Seguro',   templateName: 'cotizacion_pdf'       },
  { value: 'trazabilidad', label: 'Trazabilidad CMF',       templateName: 'trazabilidad_cmf_pdf' },
];

const API_SECRET = process.env.REACT_APP_PDF_SECRET
  || process.env.REACT_APP_CRON_SECRET
  || '';

const TOKEN_DESCRIPTIONS = {
  // Trazabilidad CMF
  nombreContratante:  'Nombre completo del contratante',
  fechaGeneracion:    'Fecha y hora de generación del documento',
  sesionFecha:        'Fecha de inicio de sesión',
  sesionIp:           'Dirección IP del usuario',
  sesionCanal:        'Canal de entrada (web, app, etc.)',
  sesionRuta:         'Ruta de entrada a la plataforma',
  sesionReferrer:     'URL de referencia de la sesión',
  sesionUserAgent:    'User Agent del navegador',
  historialRows:      'HTML con el historial de navegación',
  cotCodigo:          'Código de la cotización',
  cotFecha:           'Fecha de la cotización',
  cotCompania:        'Compañía aseguradora',
  cotProducto:        'Nombre del producto',
  cotPlan:            'Nombre del plan cotizado',
  cotPrimaTitular:    'Prima del titular en UF',
  primaCargasRows:    'Filas HTML de primas por carga familiar',
  cotPrimaTotal:      'Prima total en UF',
  cotVencimiento:     'Fecha de vencimiento de la cotización',
  actividadHtml:      'HTML con la actividad del usuario en el formulario',
  ctCodigo:           'Código de la contratación',
  ctFecha:            'Fecha de la contratación',
  ctNombre:           'Nombre del contratante',
  ctRut:              'RUT del contratante',
  ctEmail:            'Email del contratante',
  ctEstado:           'Estado de la contratación',
  subEstado:          'Estado de la suscripción',
  subFecha:           'Fecha de creación de la suscripción',
  subMonto:           'Monto de la suscripción con moneda',
  subFrecuencia:      'Frecuencia de cobro (monthly, etc.)',
  subCodigo:          'Código legible de la suscripción (SU-XXXX-XXXX)',
  subModalidad:       'Modalidad de cobro (mensual / anual / pago directo)',
  subProveedor:       'Proveedor de pago (Webpay PatPass, VirtualPOS, etc.)',
  subAutorizacion:    'Código de autorización del pago',
  subMetodoPago:      'Método / tipo de pago del procesador',
  subPlanesRows:      'Tabla de planes incluidos en la suscripción con su prima mensual',
  cerCodigo:          'Código del certificado emitido',
  cerFecha:           'Fecha de emisión del certificado',
  cerInicio:          'Fecha de inicio de cobertura',
  cerTermino:         'Fecha de término de cobertura',
  cerEmail:           'Indica si se envió el certificado por email',
  narrativaPoliza:    'Narrativa de la póliza emitida (o pendiente de emisión)',
  polEmitida:         'Estado resumido de la póliza (Emitida / Pendiente / Colectiva)',
  polNumero:          'Número de póliza emitido por la compañía',
  polPropuesta:       'Número de propuesta de la póliza',
  polEstado:          'Estado de la póliza',
  polOrigen:          'Cómo llegó la póliza (rendición / API / manual)',
  polCompania:        'Compañía emisora de la póliza',
  polInicio:          'Inicio de vigencia de la póliza',
  polFin:             'Fin de vigencia de la póliza',
  polPrimaAnual:      'Prima ANUAL de la póliza en UF (distinta del cobro mensual de la suscripción)',
  polFormaPago:       'Forma de pago de la póliza',
  polCuotas:          'Número de cuotas de la póliza',
  polRecepcion:       'Fecha de recepción de la póliza',
  // Certificado PDF
  certificateTitle:         'Título del certificado',
  certificateNumber:        'Código único del certificado, ej: CERT-YOO-ANN-123456',
  certificateStatus:        'Estado de cobertura (Activo, Pendiente, Vencido)',
  certificateStatusClass:   'Clase CSS del estado de cobertura',
  holderName:               'Nombre completo del titular',
  holderRut:                'RUT del titular',
  holderEmail:              'Email del titular',
  holderPremiumUF:          'Prima mensual del titular en UF',
  productName:              'Nombre del producto de seguro',
  planName:                 'Nombre del plan contratado',
  monthlyPremiumUF:         'Prima total mensual en UF',
  validFrom:                'Fecha de inicio de cobertura',
  validUntil:               'Fecha de término de cobertura',
  issuedDate:               'Fecha de emisión del certificado',
  subscriptionStart:        'Fecha de inicio de la suscripción',
  subscriptionStatus:       'Estado de la suscripción',
  subscriptionStatusClass:  'Clase CSS del estado de suscripción',
  companyName:              'Nombre corto de la compañía aseguradora',
  companyLegalName:         'Razón social de la compañía',
  companyRut:               'RUT de la compañía',
  companyAddress:           'Dirección de la compañía',
  companyLogoUrl:           'URL del logo de la compañía',
  companyHeaderHtml:        'HTML del header con logo y nombre de la compañía',
  companyLegalHtml:         'HTML con datos legales de la compañía',
  dependentsCount:          'Número de cargas / beneficiarios',
  coveragesTable:           'Tabla HTML de coberturas del plan',
  dependentsTable:          'Tabla HTML de cargas con prima',
  primasTable:              'Tabla HTML de desglose de primas',
  capitalUF:                'Capital asegurado en UF',
  // Cotización PDF
  cotizacionCode:   'Código único de la cotización',
  clientName:       'Nombre completo del solicitante',
  clientRut:        'RUT del solicitante',
  clientEmail:      'Correo electrónico del solicitante',
  premiumCLP:       'Prima mensual aproximada en pesos chilenos',
  benefitsTable:    'Tabla HTML de coberturas y beneficios',
  expiresAt:        'Fecha de vencimiento de la cotización',
};

// ─── Component ────────────────────────────────────────────────────────────────

export default function PdfTemplateManager() {
  // Template selection
  const [templateType, setTemplateType] = useState('certificado');
  const [templateName, setTemplateName] = useState('certificado_pdf');

  // Template source (editable)
  const [htmlSource,   setHtmlSource]   = useState('');
  const [description,  setDescription]  = useState('');
  const [loadingTpl,   setLoadingTpl]   = useState(false);
  const [saving,       setSaving]       = useState(false);
  const [savedOk,      setSavedOk]      = useState(false);
  const [tplError,     setTplError]     = useState(null);

  // Delete template
  const [deleteConfirm, setDeleteConfirm] = useState(false);

  // Token schema editing
  const [editingTokens,   setEditingTokens]   = useState(false);
  const [tokensDraft,     setTokensDraft]     = useState([]);
  const [savingTokens,    setSavingTokens]    = useState(false);
  const [tokensSavedOk,   setTokensSavedOk]   = useState(false);

  // Tokens schema
  const [tokensSchema, setTokensSchema] = useState([]);
  const [copiedToken,  setCopiedToken]  = useState(null);

  // Record search
  const [searchQuery,  setSearchQuery]  = useState('');
  const [records,      setRecords]      = useState([]);
  const [searching,    setSearching]    = useState(false);
  const [selectedId,   setSelectedId]   = useState(null);
  const [selectedLabel,setSelectedLabel]= useState('');

  // Preview
  const [activePanel,  setActivePanel]  = useState('editor'); // 'editor' | 'preview'
  const [previewHtml,  setPreviewHtml]  = useState('');
  const [loadingPreview, setLoadingPreview] = useState(false);
  const [previewError, setPreviewError] = useState(null);

  // Email modal
  const [emailModal,   setEmailModal]   = useState(false);
  const [emailTo,      setEmailTo]      = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailResult,  setEmailResult]  = useState(null);

  // New template modal
  const [newTplModal,  setNewTplModal]  = useState(false);
  const [newTplName,   setNewTplName]   = useState('');
  const [newTplLabel,  setNewTplLabel]  = useState('');
  const [newTplError,  setNewTplError]  = useState(null);

  const iframeRef = useRef(null);

  // ── Load template when type changes ────────────────────────────────────────
  useEffect(() => {
    const cfg = TEMPLATE_TYPES.find(t => t.value === templateType);
    const name = cfg ? cfg.templateName : templateType;
    setTemplateName(name);
    setSelectedId(null);
    setSelectedLabel('');
    setRecords([]);
    setSearchQuery('');
    setPreviewHtml('');
    setPreviewError(null);
    setActivePanel('editor');
  }, [templateType]);

  useEffect(() => {
    if (!templateName) return;
    loadTemplate(templateName);
  }, [templateName]);

  const loadTemplate = async (name) => {
    setLoadingTpl(true);
    setTplError(null);
    try {
      const { data, error } = await supabase
        .from('pdf_templates')
        .select('html, description, tokens_schema')
        .eq('name', name)
        .maybeSingle();
      if (error) throw error;
      if (data) {
        const html = data.html || '';
        setHtmlSource(html);
        setDescription(data.description || '');

        const saved = Array.isArray(data.tokens_schema) ? data.tokens_schema : [];
        if (saved.length > 0) {
          setTokensSchema(saved);
        } else {
          // No tokens saved yet — extract from HTML
          const found = [...new Set([...html.matchAll(/\{\{#?if\s+(\w+)\s*\}\}|\{\{(\w+)\}\}/g)]
            .map(m => m[1] || m[2])
            .filter(k => k && k !== 'if')
          )];
          setTokensSchema(found.map(key => ({ key, description: TOKEN_DESCRIPTIONS[key] ?? '' })));
        }
      } else {
        setTplError('Template no encontrado en la base de datos.');
        setDescription('');
        setTokensSchema([]);
      }
    } catch (err) {
      setTplError(err?.message || 'Error al cargar el template.');
    } finally {
      setLoadingTpl(false);
    }
  };

  // ── Save template (upsert so it works even if the DB row doesn't exist yet) ─
  const handleSave = async () => {
    setSaving(true);
    setSavedOk(false);
    setTplError(null);
    try {
      const { error } = await supabase
        .from('pdf_templates')
        .upsert(
          { name: templateName, html: htmlSource, description: description || null, updated_at: new Date().toISOString() },
          { onConflict: 'name' }
        );
      if (error) throw error;
      setSavedOk(true);
      setTimeout(() => setSavedOk(false), 3000);
    } catch (err) {
      setTplError(err?.message || 'Error al guardar.');
    } finally {
      setSaving(false);
    }
  };

  // ── Upload HTML file into editor ────────────────────────────────────────────
  const fileInputRef = useRef(null);

  const handleFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!file.name.endsWith('.html') && file.type !== 'text/html') {
      setTplError('Solo se permiten archivos .html');
      e.target.value = '';
      return;
    }
    const reader = new FileReader();
    reader.onload = (ev) => {
      const html = ev.target.result;
      setHtmlSource(html);
      setActivePanel('editor');
      setTplError(null);

      // Re-extract tokens from the new HTML, preserving existing descriptions
      const found = [...new Set([...html.matchAll(/\{\{#?if\s+(\w+)\s*\}\}|\{\{(\w+)\}\}/g)]
        .map(m => m[1] || m[2])
        .filter(k => k && k !== 'if')
      )];
      setTokensSchema(prev => {
        const existingMap = Object.fromEntries(prev.map(t => [t.key, t.description]));
        return found.map(key => ({ key, description: existingMap[key] ?? TOKEN_DESCRIPTIONS[key] ?? '' }));
      });
    };
    reader.readAsText(file, 'utf-8');
    e.target.value = '';
  };

  // ── Search records (empty query = últimos 8 registros) ─────────────────────
  const handleSearch = async (q = searchQuery) => {
    setSearching(true);
    setRecords([]);
    try {
      if (templateType === 'certificado') {
        let query = supabase
          .from('certificados')
          .select('id, codigo, fecha_emision')
          .order('fecha_emision', { ascending: false })
          .limit(8);
        if (q.trim()) query = query.or(`codigo.ilike.%${q}%,id.ilike.%${q}%`);
        const { data } = await query;
        setRecords((data || []).map(r => ({
          id:    r.id,
          label: `${r.codigo || r.id.slice(0,8)} — ${r.fecha_emision?.slice(0,10) || ''}`,
        })));
      } else if (templateType === 'cotizacion') {
        let query = supabase
          .from('cotizaciones')
          .select('id, codigo, created_at')
          .order('created_at', { ascending: false })
          .limit(8);
        if (q.trim()) query = query.or(`codigo.ilike.%${q}%,id.ilike.%${q}%`);
        const { data } = await query;
        setRecords((data || []).map(r => ({
          id:    r.id,
          label: `${r.codigo || r.id.slice(0,8)} — ${r.created_at?.slice(0,10) || ''}`,
        })));
      } else {
        // trazabilidad → busca en contrataciones
        let query = supabase
          .from('contrataciones')
          .select('id, codigo, created_at')
          .order('created_at', { ascending: false })
          .limit(8);
        if (q.trim()) query = query.or(`codigo.ilike.%${q}%,id.ilike.%${q}%`);
        const { data } = await query;
        setRecords((data || []).map(r => ({
          id:    r.id,
          label: `${r.codigo || r.id.slice(0,8)} — ${r.created_at?.slice(0,10) || ''}`,
        })));
      }
    } catch (err) {
      console.error('Search error:', err);
    } finally {
      setSearching(false);
    }
  };

  // ── Load random record ──────────────────────────────────────────────────────
  const handleLoadRandom = async () => {
    setSearching(true);
    setRecords([]);
    try {
      // Fetch up to 50 recent records, pick one at random client-side
      // (Supabase JS client doesn't support ORDER BY RANDOM() directly)
      if (templateType === 'certificado') {
        const { data } = await supabase
          .from('certificados')
          .select('id, codigo, fecha_emision')
          .order('fecha_emision', { ascending: false })
          .limit(50);
        if (data && data.length > 0) {
          const r = data[Math.floor(Math.random() * data.length)];
          setSelectedId(r.id);
          setSelectedLabel(`${r.codigo || r.id.slice(0,8)} — ${r.fecha_emision?.slice(0,10) || ''}`);
          setRecords([]);
        }
      } else if (templateType === 'cotizacion') {
        const { data } = await supabase
          .from('cotizaciones')
          .select('id, codigo, created_at')
          .order('created_at', { ascending: false })
          .limit(50);
        if (data && data.length > 0) {
          const r = data[Math.floor(Math.random() * data.length)];
          setSelectedId(r.id);
          setSelectedLabel(`${r.codigo || r.id.slice(0,8)} — ${r.created_at?.slice(0,10) || ''}`);
          setRecords([]);
        }
      } else {
        // trazabilidad
        const { data } = await supabase
          .from('contrataciones')
          .select('id, codigo, created_at')
          .order('created_at', { ascending: false })
          .limit(50);
        if (data && data.length > 0) {
          const r = data[Math.floor(Math.random() * data.length)];
          setSelectedId(r.id);
          setSelectedLabel(`${r.codigo || r.id.slice(0,8)} — ${r.created_at?.slice(0,10) || ''}`);
          setRecords([]);
        }
      }
    } catch (err) {
      console.error('Random load error:', err);
    } finally {
      setSearching(false);
    }
  };

  // ── Preview with data ───────────────────────────────────────────────────────
  const handlePreview = async () => {
    if (!selectedId) {
      // Preview editor source directly (no tokens filled)
      setPreviewHtml(htmlSource);
      setActivePanel('preview');
      return;
    }
    setLoadingPreview(true);
    setPreviewError(null);
    try {
      const res = await fetch(
        `/api/pdf/preview?type=${templateType}&id=${selectedId}`,
        { headers: { Authorization: `Bearer ${API_SECRET}` } }
      );
      if (!res.ok) {
        const j = await res.json().catch(() => ({}));
        throw new Error(j?.error || `Error ${res.status}`);
      }
      const html = await res.text();
      setPreviewHtml(html);
      setActivePanel('preview');
    } catch (err) {
      setPreviewError(err?.message || 'Error al generar preview.');
    } finally {
      setLoadingPreview(false);
    }
  };

  // Preview raw editor source on tab switch
  const handleEditorPreview = () => {
    setPreviewHtml(htmlSource);
    setActivePanel('preview');
  };

  // ── Download PDF ────────────────────────────────────────────────────────────
  const handleDownload = async () => {
    if (!selectedId) return;
    try {
      if (templateType === 'certificado') {
        await CertificatesService.downloadCertificatePdf(selectedId);
      } else {
        const type     = templateType === 'trazabilidad' ? 'trazabilidad' : 'cotizacion';
        const filename = templateType === 'trazabilidad'
          ? `trazabilidad-${selectedId.slice(0,8)}.pdf`
          : `cotizacion-${selectedId.slice(0,8)}.pdf`;
        const res = await fetch('/api/pdf/generate', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${API_SECRET}` },
          body:    JSON.stringify({ type, id: selectedId, action: 'download' }),
        });
        if (!res.ok) throw new Error(`Error ${res.status}`);
        const blob = await res.blob();
        const url  = URL.createObjectURL(blob);
        const a    = document.createElement('a');
        a.href     = url;
        a.download = filename;
        document.body.appendChild(a);
        a.click();
        a.remove();
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      alert(`Error al descargar: ${err?.message}`);
    }
  };

  // ── Send email ──────────────────────────────────────────────────────────────
  const handleSendEmail = async () => {
    if (!selectedId || !emailTo) return;
    setSendingEmail(true);
    setEmailResult(null);
    try {
      if (templateType === 'certificado') {
        await CertificatesService.generateAndEmailPdf(selectedId, emailTo);
      } else {
        const type = templateType === 'trazabilidad' ? 'trazabilidad' : 'cotizacion';
        const res = await fetch('/api/pdf/generate', {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${API_SECRET}` },
          body:    JSON.stringify({ type, id: selectedId, action: 'upload_and_email', emailTo }),
        });
        if (!res.ok) throw new Error(`Error ${res.status}`);
      }
      setEmailResult({ ok: true, msg: `PDF enviado a ${emailTo}` });
      setTimeout(() => { setEmailModal(false); setEmailResult(null); setEmailTo(''); }, 2500);
    } catch (err) {
      setEmailResult({ ok: false, msg: err?.message || 'Error al enviar.' });
    } finally {
      setSendingEmail(false);
    }
  };

  // ── Token schema editing ────────────────────────────────────────────────────
  const handleOpenTokenEditor = () => {
    // Extract all {{token}} keys from the current HTML source
    const found = [...new Set([...htmlSource.matchAll(/\{\{#?if\s+(\w+)\s*\}\}|\{\{(\w+)\}\}/g)]
      .map(m => m[1] || m[2])
      .filter(k => k && k !== 'if')
    )];

    // Merge: keep existing descriptions, add new keys without description
    const existingMap = Object.fromEntries(tokensSchema.map(t => [t.key, t.description]));
    const merged = found.map(key => ({ key, description: existingMap[key] ?? TOKEN_DESCRIPTIONS[key] ?? '' }));

    setTokensDraft(merged);
    setEditingTokens(true);
  };

  const handleTokenChange = (idx, field, value) => {
    setTokensDraft(prev => prev.map((t, i) => i === idx ? { ...t, [field]: value } : t));
  };

  const handleAddToken = () => {
    setTokensDraft(prev => [...prev, { key: '', description: '' }]);
  };

  const handleRemoveToken = (idx) => {
    setTokensDraft(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSaveTokens = async () => {
    const clean = tokensDraft.filter(t => t.key.trim());
    setSavingTokens(true);
    try {
      const { error } = await supabase
        .from('pdf_templates')
        .update({ tokens_schema: clean })
        .eq('name', templateName);
      if (error) throw error;
      setTokensSchema(clean);
      setEditingTokens(false);
      setTokensSavedOk(true);
      setTimeout(() => setTokensSavedOk(false), 2500);
    } catch (err) {
      setTplError(err?.message || 'Error al guardar tokens.');
    } finally {
      setSavingTokens(false);
    }
  };

  // ── Delete template ─────────────────────────────────────────────────────────
  const BASE_TEMPLATES = ['certificado_pdf', 'cotizacion_pdf', 'trazabilidad_cmf_pdf'];
  const isBaseTemplate = BASE_TEMPLATES.includes(templateName);

  const handleDelete = async () => {
    try {
      const { error } = await supabase
        .from('pdf_templates')
        .delete()
        .eq('name', templateName);
      if (error) throw error;
      // Remove from local list and go back to first template
      const idx = TEMPLATE_TYPES.findIndex(t => t.value === templateType);
      if (idx !== -1) TEMPLATE_TYPES.splice(idx, 1);
      setDeleteConfirm(false);
      setTemplateType('certificado');
    } catch (err) {
      setTplError(err?.message || 'Error al eliminar template.');
      setDeleteConfirm(false);
    }
  };

  // ── Create new template ─────────────────────────────────────────────────────
  const handleCreateTemplate = async () => {
    setNewTplError(null);
    const name  = newTplName.trim().toLowerCase().replace(/\s+/g, '_');
    const label = newTplLabel.trim();
    if (!name)  return setNewTplError('El nombre interno es requerido.');
    if (!label) return setNewTplError('El nombre para mostrar es requerido.');
    if (TEMPLATE_TYPES.find(t => t.templateName === name))
      return setNewTplError('Ya existe un template con ese nombre.');

    try {
      const { error } = await supabase
        .from('pdf_templates')
        .upsert({ name, html: '', updated_at: new Date().toISOString() }, { onConflict: 'name' });
      if (error) throw error;

      // Add to local list and switch to it
      TEMPLATE_TYPES.push({ value: name, label, templateName: name, isCustom: true });
      setTemplateType(name);
      setTemplateName(name);
      setHtmlSource('');
      setTokensSchema([]);
      setNewTplModal(false);
      setNewTplName('');
      setNewTplLabel('');
    } catch (err) {
      setNewTplError(err?.message || 'Error al crear template.');
    }
  };

  // ── Copy token ──────────────────────────────────────────────────────────────
  const handleCopyToken = (key) => {
    navigator.clipboard.writeText(`{{${key}}}`).then(() => {
      setCopiedToken(key);
      setTimeout(() => setCopiedToken(null), 1500);
    });
  };

  // ── Render ──────────────────────────────────────────────────────────────────
  return (
    <div className="space-y-4">
      {/* Page header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Gestor de Templates PDF</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Edita el HTML de cada documento, previsualiza con datos reales y descarga el PDF.
          </p>
        </div>
      </div>

      {/* Main 3-column grid */}
      <div className="grid grid-cols-[260px_1fr_280px] gap-4 items-start">

        {/* ── Left panel: config + actions ── */}
        <div className="space-y-3">

          {/* Template type */}
          <Card>
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm">Plantilla</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-3">
              <Select value={templateType} onValueChange={v => {
                if (v === '__new__') { setNewTplModal(true); }
                else setTemplateType(v);
              }}>
                <SelectTrigger className="h-8 text-sm">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TEMPLATE_TYPES.map(t => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                  <div className="border-t mt-1 pt-1">
                    <SelectItem value="__new__" className="text-[#007D6E] font-medium">
                      <span className="flex items-center gap-1.5">
                        <Plus className="h-3 w-3" />
                        Nuevo template…
                      </span>
                    </SelectItem>
                  </div>
                </SelectContent>
              </Select>

              <div className="flex gap-1.5">
                <Button
                  size="sm"
                  className="flex-1 h-8 text-xs"
                  onClick={handleSave}
                  disabled={saving || loadingTpl}
                >
                  {saving ? <RefreshCw className="h-3 w-3 mr-1 animate-spin" /> : <Save className="h-3 w-3 mr-1" />}
                  {savedOk ? '¡Guardado!' : 'Guardar en DB'}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 text-xs px-2"
                  onClick={() => loadTemplate(templateName)}
                  disabled={loadingTpl}
                  title="Recargar desde DB"
                >
                  <RefreshCw className={`h-3 w-3 ${loadingTpl ? 'animate-spin' : ''}`} />
                </Button>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs text-muted-foreground font-medium block mb-1">Descripción</label>
                <Input
                  placeholder="Descripción breve del template…"
                  value={description}
                  onChange={e => setDescription(e.target.value)}
                  className="h-8 text-xs"
                />
              </div>

              <input
                ref={fileInputRef}
                type="file"
                accept=".html,text/html"
                className="hidden"
                onChange={handleFileUpload}
              />
              <Button
                size="sm"
                variant="outline"
                className="w-full h-8 text-xs justify-start border-dashed text-amber-700 border-amber-300 hover:bg-amber-50"
                onClick={() => fileInputRef.current?.click()}
                title="Sube un archivo .html para cargarlo en el editor."
              >
                <Upload className="h-3 w-3 mr-2" />
                Subir archivo HTML
              </Button>

              {/* Delete — solo para templates custom */}
              {!isBaseTemplate && (
                deleteConfirm ? (
                  <div className="rounded-lg border border-red-200 bg-red-50 p-2 space-y-2">
                    <p className="text-xs text-red-700 font-medium">¿Eliminar este template de la DB?</p>
                    <div className="flex gap-1.5">
                      <Button size="sm" variant="destructive" className="flex-1 h-7 text-xs" onClick={handleDelete}>
                        Sí, eliminar
                      </Button>
                      <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => setDeleteConfirm(false)}>
                        Cancelar
                      </Button>
                    </div>
                  </div>
                ) : (
                  <Button
                    size="sm"
                    variant="outline"
                    className="w-full h-8 text-xs justify-start text-[var(--yool-error)] border-red-200 hover:bg-red-50"
                    onClick={() => setDeleteConfirm(true)}
                  >
                    <X className="h-3 w-3 mr-2" />
                    Eliminar template
                  </Button>
                )
              )}
            </CardContent>
          </Card>

          {/* Record search */}
          <Card>
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm">Registro para preview</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              <Button
                size="sm"
                variant="outline"
                className="w-full h-8 text-xs justify-start border-dashed"
                onClick={handleLoadRandom}
                disabled={searching}
              >
                {searching
                  ? <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                  : <Shuffle className="h-3 w-3 mr-2 text-[#007D6E]" />}
                Cargar ejemplo aleatorio
              </Button>

              <div className="relative flex items-center gap-0.5">
                <span className="text-xs text-muted-foreground px-1">o buscar:</span>
              </div>

              <div className="flex gap-1.5">
                <Input
                  placeholder="Código o ID..."
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSearch()}
                  onFocus={() => records.length === 0 && handleSearch('')}
                  className="h-8 text-xs flex-1"
                />
                <Button
                  size="sm"
                  variant="outline"
                  className="h-8 px-2"
                  onClick={() => handleSearch()}
                  disabled={searching}
                >
                  {searching
                    ? <RefreshCw className="h-3 w-3 animate-spin" />
                    : <Search className="h-3 w-3" />}
                </Button>
              </div>

              {records.length > 0 && (
                <div className="border rounded-lg overflow-hidden">
                  {records.map(r => (
                    <button
                      key={r.id}
                      onClick={() => { setSelectedId(r.id); setSelectedLabel(r.label); setRecords([]); }}
                      className={`w-full text-left px-3 py-2 text-xs hover:bg-muted transition-colors border-b last:border-b-0 ${selectedId === r.id ? 'bg-muted font-medium' : ''}`}
                    >
                      {r.label}
                    </button>
                  ))}
                </div>
              )}

              {selectedLabel && (
                <div className="flex items-center gap-1.5 p-2 bg-muted rounded-lg text-xs">
                  <CheckCheck className="h-3 w-3 text-[var(--yool-success)] shrink-0" />
                  <span className="truncate font-mono">{selectedLabel}</span>
                </div>
              )}
            </CardContent>
          </Card>

          {/* Actions */}
          <Card>
            <CardHeader className="pb-2 pt-4 px-4">
              <CardTitle className="text-sm">Acciones</CardTitle>
            </CardHeader>
            <CardContent className="px-4 pb-4 space-y-2">
              <Button
                size="sm"
                variant="outline"
                className="w-full h-8 text-xs justify-start"
                onClick={handlePreview}
                disabled={loadingPreview}
              >
                {loadingPreview
                  ? <RefreshCw className="h-3 w-3 mr-2 animate-spin" />
                  : <Eye className="h-3 w-3 mr-2" />}
                {selectedId ? 'Preview con datos reales' : 'Preview sin datos'}
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="w-full h-8 text-xs justify-start"
                onClick={handleDownload}
                disabled={!selectedId}
                title={!selectedId ? 'Selecciona un registro primero' : ''}
              >
                <Download className="h-3 w-3 mr-2" />
                Descargar PDF
              </Button>

              <Button
                size="sm"
                variant="outline"
                className="w-full h-8 text-xs justify-start"
                onClick={() => setEmailModal(true)}
                disabled={!selectedId}
                title={!selectedId ? 'Selecciona un registro primero' : ''}
              >
                <Mail className="h-3 w-3 mr-2" />
                Enviar por email
              </Button>
            </CardContent>
          </Card>

          {previewError && (
            <Alert variant="destructive" className="py-2 px-3">
              <AlertCircle className="h-3 w-3" />
              <AlertDescription className="text-xs ml-2">{previewError}</AlertDescription>
            </Alert>
          )}
          {tplError && (
            <Alert variant="destructive" className="py-2 px-3">
              <AlertCircle className="h-3 w-3" />
              <AlertDescription className="text-xs ml-2">{tplError}</AlertDescription>
            </Alert>
          )}
        </div>

        {/* ── Center panel: editor / preview ── */}
        <Card className="min-h-[600px] flex flex-col">
          {/* Tab bar */}
          <div className="flex items-center border-b px-4 pt-3 pb-0 gap-1">
            <button
              onClick={() => setActivePanel('editor')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                activePanel === 'editor'
                  ? 'border-[#007D6E] text-[#007D6E]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Code2 className="h-3.5 w-3.5" />
              Editor HTML
            </button>
            <button
              onClick={activePanel === 'editor' ? handleEditorPreview : () => setActivePanel('preview')}
              className={`flex items-center gap-1.5 px-3 py-2 text-xs font-medium border-b-2 transition-colors ${
                activePanel === 'preview'
                  ? 'border-[#007D6E] text-[#007D6E]'
                  : 'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              <Eye className="h-3.5 w-3.5" />
              Preview
            </button>
            <div className="ml-auto flex items-center gap-2 pb-1">
              {savedOk && (
                <Badge variant="outline" className="text-green-700 border-green-300 bg-green-50 text-xs h-5">
                  ¡Guardado!
                </Badge>
              )}
              {loadingTpl && (
                <RefreshCw className="h-3.5 w-3.5 animate-spin text-muted-foreground" />
              )}
            </div>
          </div>

          {/* Editor */}
          {activePanel === 'editor' && (
            <div className="flex-1 p-3">
              <textarea
                value={htmlSource}
                onChange={e => setHtmlSource(e.target.value)}
                disabled={loadingTpl}
                spellCheck={false}
                className="w-full h-full min-h-[560px] font-mono text-xs bg-slate-950 text-slate-100 rounded-lg p-4 resize-none focus:outline-none focus:ring-2 focus:ring-[#007D6E]/40 border border-slate-800"
                placeholder="Cargando template..."
              />
            </div>
          )}

          {/* Preview iframe */}
          {activePanel === 'preview' && (
            <div className="flex-1 p-3">
              {previewHtml ? (
                <iframe
                  ref={iframeRef}
                  srcDoc={previewHtml}
                  title="PDF Preview"
                  className="w-full h-full min-h-[560px] rounded-lg border border-slate-200 bg-white"
                  sandbox="allow-same-origin"
                />
              ) : (
                <div className="flex flex-col items-center justify-center h-full min-h-[560px] text-muted-foreground gap-3">
                  <Eye className="h-10 w-10 opacity-20" />
                  <p className="text-sm">
                    {selectedId
                      ? 'Haz click en "Preview con datos reales" para ver el documento renderizado.'
                      : 'Selecciona un registro en el panel izquierdo para previsualizar con datos reales.'}
                  </p>
                </div>
              )}
            </div>
          )}
        </Card>

        {/* ── Right panel: token reference ── */}
        <Card>
          <CardHeader className="pb-2 pt-4 px-4">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm flex items-center gap-2">
                <FileText className="h-4 w-4 text-[#007D6E]" />
                Variables disponibles
              </CardTitle>
              {!editingTokens ? (
                <button
                  onClick={handleOpenTokenEditor}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                  title="Editar variables"
                >
                  <Pencil className="h-3 w-3" />
                  Editar
                </button>
              ) : (
                <div className="flex items-center gap-1.5">
                  {tokensSavedOk && <CheckCheck className="h-3.5 w-3.5 text-[var(--yool-success)]" />}
                  <button
                    onClick={handleSaveTokens}
                    disabled={savingTokens}
                    className="flex items-center gap-1 text-xs text-[#007D6E] font-semibold hover:opacity-80 transition-opacity"
                  >
                    {savingTokens ? <RefreshCw className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                    Guardar
                  </button>
                  <button
                    onClick={() => setEditingTokens(false)}
                    className="text-muted-foreground hover:text-foreground transition-colors ml-1"
                  >
                    <X className="h-3.5 w-3.5" />
                  </button>
                </div>
              )}
            </div>
            {!editingTokens && (
              <p className="text-xs text-muted-foreground mt-0.5">
                Click en una variable para copiarla.
              </p>
            )}
          </CardHeader>
          <CardContent className="px-3 pb-4">

            {/* ── View mode ── */}
            {!editingTokens && (
              <>
                {tokensSchema.length === 0 && (
                  <p className="text-xs text-muted-foreground px-1 mb-2">
                    Sin variables definidas.{' '}
                    <button onClick={handleOpenTokenEditor} className="text-[#007D6E] underline">Agregar</button>
                  </p>
                )}
                <div className="space-y-1">
                  {tokensSchema.map(t => (
                    <button
                      key={t.key}
                      onClick={() => handleCopyToken(t.key)}
                      className="w-full text-left group hover:bg-muted rounded-lg p-2 transition-colors"
                    >
                      <div className="flex items-center justify-between gap-1">
                        <code className="text-xs font-mono text-[#007D6E] font-semibold">
                          {`{{${t.key}}}`}
                        </code>
                        {copiedToken === t.key
                          ? <CheckCheck className="h-3 w-3 text-[var(--yool-success)] shrink-0" />
                          : <Copy className="h-3 w-3 text-muted-foreground shrink-0 opacity-0 group-hover:opacity-100 transition-opacity" />
                        }
                      </div>
                      <div className="text-xs text-muted-foreground mt-0.5 leading-snug">{t.description}</div>
                    </button>
                  ))}
                </div>
              </>
            )}

            {/* ── Edit mode ── */}
            {editingTokens && (
              <div className="space-y-2">
                {tokensDraft.map((t, idx) => (
                  <div key={idx} className="rounded-lg border border-slate-200 bg-slate-50 p-2 space-y-1.5">
                    <code className="text-xs font-mono text-[#007D6E] font-semibold block">{`{{${t.key}}}`}</code>
                    <Input
                      placeholder="Descripción de la variable…"
                      value={t.description}
                      onChange={e => handleTokenChange(idx, 'description', e.target.value)}
                      className="h-7 text-xs"
                    />
                  </div>
                ))}
                {tokensDraft.length === 0 && (
                  <p className="text-xs text-muted-foreground text-center py-2">
                    No se encontraron variables en el HTML.
                  </p>
                )}
              </div>
            )}

          </CardContent>
        </Card>
      </div>

      {/* ── New template modal ── */}
      {newTplModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-sm mx-4">
            <CardHeader className="pb-3">
              <div className="flex items-center justify-between">
                <CardTitle className="flex items-center gap-2 text-base">
                  <Plus className="h-4 w-4" />
                  Nuevo template PDF
                </CardTitle>
                <button
                  onClick={() => { setNewTplModal(false); setNewTplName(''); setNewTplLabel(''); setNewTplError(null); }}
                  className="text-muted-foreground hover:text-foreground transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              </div>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">
                  Nombre para mostrar
                </label>
                <Input
                  placeholder="Ej: Póliza de Vida"
                  value={newTplLabel}
                  onChange={e => setNewTplLabel(e.target.value)}
                  className="h-9"
                  autoFocus
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">
                  Nombre interno (key en DB)
                </label>
                <Input
                  placeholder="Ej: poliza_vida_pdf"
                  value={newTplName}
                  onChange={e => setNewTplName(e.target.value.toLowerCase().replace(/\s+/g, '_'))}
                  onKeyDown={e => e.key === 'Enter' && handleCreateTemplate()}
                  className="h-9 font-mono text-sm"
                />
                <p className="text-xs text-muted-foreground mt-1">Solo minúsculas, números y guiones bajos.</p>
              </div>

              {newTplError && (
                <Alert variant="destructive" className="py-2">
                  <AlertDescription className="text-xs">{newTplError}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2 pt-1">
                <Button size="sm" className="flex-1" onClick={handleCreateTemplate}>
                  <Plus className="h-3.5 w-3.5 mr-2" />
                  Crear template
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setNewTplModal(false); setNewTplName(''); setNewTplLabel(''); setNewTplError(null); }}
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}

      {/* ── Email modal ── */}
      {emailModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 backdrop-blur-sm">
          <Card className="w-full max-w-sm mx-4">
            <CardHeader className="pb-3">
              <CardTitle className="flex items-center gap-2 text-base">
                <Mail className="h-4 w-4" />
                Enviar PDF por email
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <div>
                <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide block mb-1">
                  Destinatario
                </label>
                <Input
                  type="email"
                  placeholder="cliente@email.com"
                  value={emailTo}
                  onChange={e => setEmailTo(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && handleSendEmail()}
                  className="h-9"
                />
              </div>

              {emailResult && (
                <Alert variant={emailResult.ok ? 'default' : 'destructive'} className="py-2">
                  <AlertDescription className="text-xs">{emailResult.msg}</AlertDescription>
                </Alert>
              )}

              <div className="flex gap-2 pt-1">
                <Button
                  size="sm"
                  className="flex-1"
                  onClick={handleSendEmail}
                  disabled={sendingEmail || !emailTo}
                >
                  {sendingEmail
                    ? <><RefreshCw className="h-3.5 w-3.5 mr-2 animate-spin" />Enviando...</>
                    : <><Mail className="h-3.5 w-3.5 mr-2" />Enviar</>}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => { setEmailModal(false); setEmailResult(null); setEmailTo(''); }}
                  disabled={sendingEmail}
                >
                  Cancelar
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
